import React, { useState, useEffect } from "react";
import { makeRequest2 } from "../../../../../utils/makeRequest";
import { toastify } from "../../../../../utils/toast";

const DutyRosterModal = ({
  show,
  onClose,
  onSuccess,
  facilityId,
  roster,
  staffUsers,
  masterWorkplans,
}) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    staffId: "",
    staffPosition: "Normal",
    masterWorkplanId: "",
    startDate: "",
    endDate: "",
    weeklySchedule: {
      monday: [{ startTime: "08:00", endTime: "17:00", status: "ON" }],
      tuesday: [{ startTime: "08:00", endTime: "17:00", status: "ON" }],
      wednesday: [{ startTime: "08:00", endTime: "17:00", status: "ON" }],
      thursday: [{ startTime: "08:00", endTime: "17:00", status: "ON" }],
      friday: [{ startTime: "08:00", endTime: "17:00", status: "ON" }],
      saturday: [{ startTime: "08:00", endTime: "13:00", status: "ON" }],
      sunday: [{ startTime: "", endTime: "", status: "OFF" }],
    },
    exceptions: [],
    // NEW: Checklist generation options
    checklistOptions: {
      generateChecklist: true,
      taskFrequency: "daily",
    },
  });

  const [selectedDates, setSelectedDates] = useState([]);
  const [showChecklistOptions, setShowChecklistOptions] = useState(true);

  useEffect(() => {
    if (roster) {
      // Format existing roster data
      let formattedWeeklySchedule = {};
      if (roster.weeklySchedule) {
        Object.keys(roster.weeklySchedule).forEach((day) => {
          if (Array.isArray(roster.weeklySchedule[day])) {
            formattedWeeklySchedule[day] = roster.weeklySchedule[day];
          } else if (roster.weeklySchedule[day]) {
            formattedWeeklySchedule[day] = [roster.weeklySchedule[day]];
          } else {
            formattedWeeklySchedule[day] = [
              { startTime: "", endTime: "", status: "ON" },
            ];
          }
        });
      } else {
        formattedWeeklySchedule = initializeWeeklySchedule();
      }

      setFormData({
        staffId: roster.staffId?._id || roster.staffId || "",
        staffPosition: roster.staffPosition || "Normal",
        masterWorkplanId:
          roster.masterWorkplanId?._id || roster.masterWorkplanId || "",
        startDate: roster.startDate
          ? new Date(roster.startDate).toISOString().split("T")[0]
          : "",
        endDate: roster.endDate
          ? new Date(roster.endDate).toISOString().split("T")[0]
          : "",
        weeklySchedule: formattedWeeklySchedule,
        exceptions: roster.exceptions || [],
        checklistOptions: {
          generateChecklist: true, // Keep enabled for updates
          taskFrequency: "daily",
        },
      });

      // Set selected exception dates
      if (roster.exceptions && roster.exceptions.length > 0) {
        setSelectedDates(roster.exceptions.map((ex) => new Date(ex.date)));
      }

      // For existing rosters, hide checklist options by default
      setShowChecklistOptions(false);
    } else {
      // Reset for new roster
      setFormData({
        staffId: "",
        staffPosition: "Normal",
        masterWorkplanId: "",
        startDate: new Date().toISOString().split("T")[0],
        endDate: new Date(new Date().setMonth(new Date().getMonth() + 1))
          .toISOString()
          .split("T")[0],
        weeklySchedule: initializeWeeklySchedule(),
        exceptions: [],
        checklistOptions: {
          generateChecklist: true,
          taskFrequency: "daily",
        },
      });
      setSelectedDates([]);
      setShowChecklistOptions(true); // Show for new rosters
    }
  }, [roster]);

  const initializeWeeklySchedule = () => ({
    monday: [{ startTime: "08:00", endTime: "17:00", status: "ON" }],
    tuesday: [{ startTime: "08:00", endTime: "17:00", status: "ON" }],
    wednesday: [{ startTime: "08:00", endTime: "17:00", status: "ON" }],
    thursday: [{ startTime: "08:00", endTime: "17:00", status: "ON" }],
    friday: [{ startTime: "08:00", endTime: "17:00", status: "ON" }],
    saturday: [{ startTime: "08:00", endTime: "13:00", status: "ON" }],
    sunday: [{ startTime: "", endTime: "", status: "OFF" }],
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // NEW: Handle checklist options change
  const handleChecklistOptionChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      checklistOptions: {
        ...prev.checklistOptions,
        [field]: value,
      },
    }));
  };

  const handleWeeklyScheduleChange = (day, index, field, value) => {
    setFormData((prev) => {
      const updatedSchedule = { ...prev.weeklySchedule };
      const updatedDaySchedule = [...updatedSchedule[day]];
      updatedDaySchedule[index] = {
        ...updatedDaySchedule[index],
        [field]: value,
      };
      updatedSchedule[day] = updatedDaySchedule;

      return {
        ...prev,
        weeklySchedule: updatedSchedule,
      };
    });
  };

  const addTimeSlot = (day) => {
    setFormData((prev) => {
      const updatedSchedule = { ...prev.weeklySchedule };
      updatedSchedule[day] = [
        ...updatedSchedule[day],
        { startTime: "", endTime: "", status: "ON" },
      ];
      return {
        ...prev,
        weeklySchedule: updatedSchedule,
      };
    });
  };

  const removeTimeSlot = (day, index) => {
    setFormData((prev) => {
      const updatedSchedule = { ...prev.weeklySchedule };
      if (updatedSchedule[day].length > 1) {
        updatedSchedule[day] = updatedSchedule[day].filter(
          (_, i) => i !== index,
        );
      }
      return {
        ...prev,
        weeklySchedule: updatedSchedule,
      };
    });
  };

  const addExceptionDate = (date) => {
    const selectedDate = new Date(date);
    const dateExists = selectedDates.some(
      (existingDate) =>
        existingDate.toDateString() === selectedDate.toDateString(),
    );
    if (!dateExists) {
      setSelectedDates([...selectedDates, selectedDate]);
    }
  };

  const removeExceptionDate = (index) => {
    setSelectedDates(selectedDates.filter((_, i) => i !== index));
  };

  const getDayOfWeek = (date) => {
    const days = [
      "sunday",
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
    ];
    return days[new Date(date).getDay()];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Prepare exceptions from selected dates
      const exceptions = selectedDates.map((date) => {
        const dayOfWeek = getDayOfWeek(date);
        const daySchedule = formData.weeklySchedule[dayOfWeek];
        const defaultSlot =
          daySchedule && daySchedule.length > 0
            ? daySchedule[0]
            : { startTime: "", endTime: "", status: "ON" };

        return {
          date: date,
          startTime: defaultSlot.startTime,
          endTime: defaultSlot.endTime,
          status: "AL", // Default to Annual Leave
          reason: "Exception",
        };
      });

      const submitData = {
        staffId: formData.staffId,
        staffPosition: formData.staffPosition,
        masterWorkplanId: formData.masterWorkplanId,
        startDate: formData.startDate,
        endDate: formData.endDate,
        weeklySchedule: formData.weeklySchedule,
        exceptions: exceptions,
        // Include checklist options for new rosters or when explicitly enabled
        ...((!roster || showChecklistOptions) && {
          checklistOptions: formData.checklistOptions,
        }),
      };

      let response;
      if (roster) {
        response = await makeRequest2(
          `/api/duty_roster/${facilityId}/${roster._id}`,
          "PUT",
          submitData,
        );
      } else {
        response = await makeRequest2(
          `/api/duty_roster/${facilityId}`,
          "POST",
          submitData,
        );
      }

      if (response.success) {
        onSuccess();
      } else {
        toastify(response.error || "An error occurred", "error");
      }
    } catch (error) {
      toastify("An error occurred", "error");
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  return (
    <div
      className="modal d-block"
      tabIndex="-1"
      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
    >
      <div className="modal-dialog modal-xl modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              {roster ? "Edit Duty Roster" : "Add Duty Roster"}
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
            ></button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              <div className="row">
                {/* Left Column - Staff and Basic Info */}
                <div className="col-md-4">
                  <div className="mb-3">
                    <label className="form-label">Staff Member *</label>
                    <select
                      className="form-select"
                      name="staffId"
                      value={formData.staffId}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Select Staff</option>
                      {staffUsers.map((user) => (
                        <option key={user._id} value={user._id}>
                          {user.fullName || user.name} - {user.role}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Staff Position</label>
                    <select
                      className="form-select"
                      name="staffPosition"
                      value={formData.staffPosition}
                      onChange={handleInputChange}
                    >
                      <option value="Normal">Normal</option>
                      <option value="Assistant">Assistant</option>
                      <option value="Deputy">Deputy</option>
                      <option value="Head">Head</option>
                    </select>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Master Workplan *</label>
                    <select
                      className="form-select"
                      name="masterWorkplanId"
                      value={formData.masterWorkplanId}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Select Master Workplan</option>
                      {masterWorkplans.map((workplan) => (
                        <option key={workplan._id} value={workplan._id}>
                          {workplan.title}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Start Date *</label>
                    <input
                      type="date"
                      className="form-control"
                      name="startDate"
                      value={formData.startDate}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">End Date *</label>
                    <input
                      type="date"
                      className="form-control"
                      name="endDate"
                      value={formData.endDate}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  {/* NEW: Checklist Generation Options */}
                  {(!roster || showChecklistOptions) && (
                    <div className="mb-3">
                      <div className="card bg-light">
                        <div className="card-header d-flex justify-content-between align-items-center">
                          <label className="form-label mb-0">
                            Checklist Options
                          </label>
                          {roster && (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() =>
                                setShowChecklistOptions(!showChecklistOptions)
                              }
                            >
                              {showChecklistOptions ? "Hide" : "Show"}
                            </button>
                          )}
                        </div>
                        <div className="card-body">
                          <div className="form-check mb-2">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id="generateChecklist"
                              checked={
                                formData.checklistOptions.generateChecklist
                              }
                              onChange={(e) =>
                                handleChecklistOptionChange(
                                  "generateChecklist",
                                  e.target.checked,
                                )
                              }
                            />
                            <label
                              className="form-check-label"
                              htmlFor="generateChecklist"
                            >
                              Generate Task Checklist
                            </label>
                          </div>

                          {formData.checklistOptions.generateChecklist && (
                            <div className="mb-2">
                              <label className="form-label">
                                Task Frequency
                              </label>
                              <select
                                className="form-select form-select-sm"
                                value={formData.checklistOptions.taskFrequency}
                                onChange={(e) =>
                                  handleChecklistOptionChange(
                                    "taskFrequency",
                                    e.target.value,
                                  )
                                }
                              >
                                <option value="daily">Daily</option>
                                <option value="weekly">Weekly</option>
                                <option value="monthly">Monthly</option>
                              </select>
                            </div>
                          )}

                          <small className="text-muted">
                            {formData.checklistOptions.generateChecklist
                              ? `A task checklist will be created with ${formData.checklistOptions.taskFrequency} scheduling for all child tasks in the selected master workplan.`
                              : "No checklist will be generated. You can create one later if needed."}
                          </small>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Exception Dates */}
                  <div className="mb-3">
                    <label className="form-label">Add Exception Date</label>
                    <input
                      type="date"
                      className="form-control"
                      onChange={(e) => {
                        if (e.target.value) {
                          addExceptionDate(e.target.value);
                          e.target.value = "";
                        }
                      }}
                    />
                    <small className="form-text text-muted">
                      Add dates that differ from regular schedule
                    </small>
                  </div>

                  {selectedDates.length > 0 && (
                    <div className="mb-3">
                      <label className="form-label">Exception Dates:</label>
                      <div className="list-group list-group-flush">
                        {selectedDates.map((date, index) => (
                          <div
                            key={index}
                            className="list-group-item d-flex justify-content-between align-items-center p-2"
                          >
                            <span>
                              {date.toLocaleDateString()} ({getDayOfWeek(date)})
                            </span>
                            <button
                              type="button"
                              className="btn btn-sm btn-danger"
                              onClick={() => removeExceptionDate(index)}
                            >
                              <i className="ti ti-trash"></i>
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Right Column - Weekly Schedule */}
                <div className="col-md-8">
                  <div className="mb-3">
                    <label className="form-label">Weekly Schedule</label>
                    <div className="table-responsive">
                      <table className="table table-sm table-bordered">
                        <thead>
                          <tr>
                            <th>Day</th>
                            <th>Time Slots</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.keys(formData.weeklySchedule).map((day) => (
                            <tr key={day}>
                              <td className="text-capitalize align-middle">
                                <strong>{day}</strong>
                              </td>
                              <td>
                                {formData.weeklySchedule[day].map(
                                  (slot, index) => (
                                    <div
                                      key={index}
                                      className="d-flex mb-2 align-items-center gap-2"
                                    >
                                      <input
                                        type="time"
                                        className="form-control form-control-sm"
                                        value={slot.startTime}
                                        onChange={(e) =>
                                          handleWeeklyScheduleChange(
                                            day,
                                            index,
                                            "startTime",
                                            e.target.value,
                                          )
                                        }
                                      />
                                      <span>to</span>
                                      <input
                                        type="time"
                                        className="form-control form-control-sm"
                                        value={slot.endTime}
                                        onChange={(e) =>
                                          handleWeeklyScheduleChange(
                                            day,
                                            index,
                                            "endTime",
                                            e.target.value,
                                          )
                                        }
                                      />
                                      <select
                                        className="form-select form-select-sm"
                                        value={slot.status}
                                        onChange={(e) =>
                                          handleWeeklyScheduleChange(
                                            day,
                                            index,
                                            "status",
                                            e.target.value,
                                          )
                                        }
                                      >
                                        <option value="ON">On Duty</option>
                                        <option value="OFF">Off Duty</option>
                                        <option value="AL">Annual Leave</option>
                                        <option value="CL">Casual Leave</option>
                                        <option value="ML/PL">
                                          Medical/Paternity Leave
                                        </option>
                                        <option value="PH">
                                          Public Holiday
                                        </option>
                                        <option value="UI">
                                          Unplanned Issues
                                        </option>
                                      </select>
                                      {formData.weeklySchedule[day].length >
                                        1 && (
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-danger"
                                          onClick={() =>
                                            removeTimeSlot(day, index)
                                          }
                                        >
                                          <i className="ti ti-trash"></i>
                                        </button>
                                      )}
                                    </div>
                                  ),
                                )}
                              </td>
                              <td className="align-middle">
                                <button
                                  type="button"
                                  className="btn btn-sm btn-primary"
                                  onClick={() => addTimeSlot(day)}
                                >
                                  <i className="ti ti-plus"></i>
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={
                  loading || !formData.staffId || !formData.masterWorkplanId
                }
              >
                {loading && (
                  <span className="spinner-border spinner-border-sm me-2"></span>
                )}
                {roster ? "Update" : "Create"} Duty Roster
                {!roster && formData.checklistOptions.generateChecklist && (
                  <small className="d-block">& Generate Checklist</small>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default DutyRosterModal;
